/**
 * Merchants/DBA slice - single source for merchant API
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/config';
import { DBAS, STATUSES, LEADS } from '../../api/endpoints';

export const getDBAs = createAsyncThunk(
  'merchants/getDBAs',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(DBAS.LIST, { params });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch DBA data');
    }
  }
);

export const getDBAById = createAsyncThunk(
  'merchants/getDBAById',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(DBAS.BY_ID(id));
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch DBA details');
    }
  }
);

export const submitAllStepperData = createAsyncThunk(
  'merchants/submitAllStepperData',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post(DBAS.LIST, payload);
      return data;
    } catch (err) {
      const payload = err.response?.data;
      return rejectWithValue(payload ?? { message: 'Failed to submit stepper data', errors: {} });
    }
  }
);

export const updateDBA = createAsyncThunk(
  'merchants/updateDBA',
  async ({ id, dbaData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(DBAS.BY_ID(id), dbaData);
      return data;
    } catch (err) {
      const errData = err.response?.data;
      return rejectWithValue(errData ?? { message: 'Failed to update DBA', errors: {} });
    }
  }
);

export const getDbaStatus = createAsyncThunk(
  'merchants/getDbaStatus',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/statuses', { params });
      return data?.data?.list || data?.list || [];
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch statuses');
    }
  }
);

export const updateDbaProcessStatus = createAsyncThunk(
  'merchants/updateDbaProcessStatus',
  async ({ id, statusId }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(DBAS.PROCESS_STATUS(id), { status_id: statusId });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update status');
    }
  }
);

export const updateDbaStatus = createAsyncThunk(
  'merchants/updateDbaStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/dbas/${id}/status`, { status });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update status');
    }
  }
);

export const assignDbaToUser = createAsyncThunk(
  'merchants/assignDbaToUser',
  async ({ id, userId }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(DBAS.REASSIGN(id), { user_id: userId });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to assign DBA');
    }
  }
);

export const deleteDba = createAsyncThunk(
  'merchants/deleteDba',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/dbas/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete DBA');
    }
  }
);

export const getLeadsForMerchant = createAsyncThunk(
  'merchants/getLeadsForMerchant',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(LEADS.LIST, { params });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch leads');
    }
  }
);

const initialState = {
  dbas: null,
  currentDba: null,
  statuses: [],
  leads: null,
  loading: false,
  error: null,
};

const merchantsSlice = createSlice({
  name: 'merchants',
  initialState,
  reducers: {
    clearCurrentDba: (state) => {
      state.currentDba = null;
    },
    clearMerchantsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    const handlePending = (state) => {
      state.loading = true;
      state.error = null;
    };
    const handleRejected = (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Something went wrong';
    };

    builder
      .addCase(getDBAs.fulfilled, (state, action) => {
        state.loading = false;
        state.dbas = action.payload;
      })
      .addCase(getDBAById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentDba = action.payload;
      })
      .addCase(getDbaStatus.fulfilled, (state, action) => {
        state.statuses = action.payload;
      })
      .addCase(getLeadsForMerchant.fulfilled, (state, action) => {
        state.leads = action.payload;
      })
      .addMatcher(
        (action) => {
          const thunks = [
            getDBAs.pending,
            getDBAById.pending,
            getDbaStatus.pending,
            getLeadsForMerchant.pending,
            submitAllStepperData.pending,
            updateDBA.pending,
            updateDbaProcessStatus.pending,
            updateDbaStatus.pending,
            assignDbaToUser.pending,
            deleteDba.pending,
          ];
          return thunks.some((ac) => ac && typeof ac.match === 'function' && ac.match(action));
        },
        handlePending
      )
      .addMatcher(
        (action) => {
          const thunks = [
            getDBAs.rejected,
            getDBAById.rejected,
            getDbaStatus.rejected,
            getLeadsForMerchant.rejected,
            submitAllStepperData.rejected,
            updateDBA.rejected,
            updateDbaProcessStatus.rejected,
            updateDbaStatus.rejected,
            assignDbaToUser.rejected,
            deleteDba.rejected,
          ];
          return thunks.some((ac) => ac && typeof ac.match === 'function' && ac.match(action));
        },
        handleRejected
      )
      .addMatcher(
        (action) => {
          const thunks = [
            getLeadsForMerchant.fulfilled,
            submitAllStepperData.fulfilled,
            updateDBA.fulfilled,
            updateDbaProcessStatus.fulfilled,
            updateDbaStatus.fulfilled,
            assignDbaToUser.fulfilled,
            deleteDba.fulfilled,
          ];
          return thunks.some((ac) => ac && typeof ac.match === 'function' && ac.match(action));
        },
        (state) => {
          state.loading = false;
        }
      );
  },
});

export const { clearCurrentDba, clearMerchantsError } = merchantsSlice.actions;
export default merchantsSlice.reducer;
